import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import {
  loadDiyCategories,
  getDiyActivitiesForCategory,
  type DiyCategory as DiyCategoryType,
  type DiyActivity,
} from '@/services/content'
import styles from './DIYCategory.module.css'

const AGE_FILTERS = [
  { label: 'All', min: 0, max: 999 },
  { label: '1-2 yrs', min: 12, max: 24 },
  { label: '2-3 yrs', min: 24, max: 36 },
  { label: '3-4 yrs', min: 36, max: 48 },
  { label: '4-6 yrs', min: 48, max: 72 },
]

function getDefaultFilterIndex(ageMonths: number | undefined): number {
  if (ageMonths === undefined) return 0
  for (let i = 1; i < AGE_FILTERS.length; i++) {
    if (ageMonths >= AGE_FILTERS[i].min && ageMonths < AGE_FILTERS[i].max) {
      return i
    }
  }
  return 0
}

export default function DIYCategory() {
  const navigate = useNavigate()
  const { categoryId } = useParams<{ categoryId: string }>()
  const user = useAuthStore((state) => state.user)
  const childAgeMonths = user?.child_profile?.age_months

  const [category, setCategory] = useState<DiyCategoryType | null>(null)
  const [allActivities, setAllActivities] = useState<DiyActivity[]>([])
  const [filteredActivities, setFilteredActivities] = useState<DiyActivity[]>([])
  const [activeFilter, setActiveFilter] = useState(() => getDefaultFilterIndex(childAgeMonths))
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!categoryId) return
    Promise.all([
      loadDiyCategories(),
      getDiyActivitiesForCategory(categoryId),
    ])
      .then(([cats, acts]) => {
        const cat = cats.find(c => c.id === categoryId)
        if (cat) setCategory(cat)
        setAllActivities(acts)
      })
      .finally(() => setLoading(false))
  }, [categoryId])

  useEffect(() => {
    const filter = AGE_FILTERS[activeFilter]
    if (activeFilter === 0) {
      setFilteredActivities(allActivities)
    } else {
      setFilteredActivities(
        allActivities.filter(a =>
          a.ageRange.max >= filter.min && a.ageRange.min <= filter.max
        )
      )
    }
  }, [activeFilter, allActivities])

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }

  if (!category) {
    return (
      <div className={styles.container}>
        <p>Category not found.</p>
        <button className={styles.backButton} onClick={() => navigate('/diy')}>
          ← Back to all
        </button>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <header
        className={styles.header}
        style={{ backgroundColor: category.color }}
      >
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          ←
        </button>
        <span className={styles.headerEmoji}>{category.emoji}</span>
        <h1 className={styles.headerTitle}>{category.title}</h1>
        <p className={styles.headerDesc}>{category.description}</p>
      </header>

      <div className={styles.filters}>
        {AGE_FILTERS.map((filter, index) => (
          <button
            key={filter.label}
            className={`${styles.filterChip} ${activeFilter === index ? styles.filterChipActive : ''}`}
            onClick={() => setActiveFilter(index)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {filteredActivities.length === 0 ? (
        <div className={styles.empty}>
          No activities for this age range. Try "All" to see everything.
        </div>
      ) : (
        <div className={styles.activityList}>
          {filteredActivities.map((activity) => (
            <button
              key={activity.id}
              className={styles.activityCard}
              onClick={() => navigate(`/activity/${activity.id}`)}
            >
              <div className={styles.activityInfo}>
                <span className={styles.activityTitle}>{activity.title}</span>
                <div className={styles.activityMeta}>
                  <span className={styles.metaChip}>{activity.duration} min</span>
                  <span className={styles.metaChip}>{activity.props.length} items</span>
                </div>
              </div>
              <span className={styles.activityArrow}>→</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
